import axios from 'axios';
import toast from 'react-hot-toast';

const API_URL = process.env.REACT_APP_API_URL || 'https://multi-schoolsuite.cbthost.com';

// Create base axios instance WITH interceptors
const createBaseAPI = () => {
  const instance = axios.create({
    baseURL: API_URL,
  });

  // Request interceptor for adding token
  instance.interceptors.request.use(
    (config) => {
      const token = localStorage.getItem('token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // Response interceptor for handling 401
  instance.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error.response?.status === 401) {
        toast.error('Session expired. Please login again.');
        localStorage.removeItem('token');
        localStorage.removeItem('student_user');
        localStorage.removeItem('user');
        window.location.href = '/student_login';
      }
      return Promise.reject(error);
    }
  );

  return instance;
};

// Create instances with proper interceptors
const staffAPI = createBaseAPI();
const studentAPI = createBaseAPI();

// Export the instances for manual use if needed
export { staffAPI, studentAPI };

// ==================== Admin Quiz Management ====================
export const adminQuizAPI = {
  // Quiz CRUD
  getQuizzes: () => staffAPI.get('/admin/quizzes'),
  getMyQuizzes: () => staffAPI.get('/admin/quizzes/my'),
  getQuiz: (quizId) => staffAPI.get(`/admin/quizzes/${quizId}`),
  getTeacherQuiz: (quizId) => staffAPI.get(`/admin/quizzes/teacher/${quizId}`),
  createQuiz: (data) => staffAPI.post('/admin/quizzes', data),
  updateQuiz: (quizId, data) => staffAPI.put(`/admin/quizzes/${quizId}`, data),
  updateTeacherQuiz: (quizId, data) => staffAPI.put(`/admin/quizzes/teacher/${quizId}`, data),
  deleteQuiz: (quizId) => staffAPI.delete(`/admin/quizzes/${quizId}`),
  deleteTeacherQuiz: (quizId) => staffAPI.delete(`/admin/quizzes/teacher/${quizId}`),
  toggleQuizStatus: (quizId, status) => staffAPI.put(`/admin/quizzes/${quizId}/status`, { status }),
  
  // Questions
  getQuizQuestions: (quizId) => staffAPI.get(`/admin/quizzes/${quizId}/questions`),
  createQuizQuestionsBulk: (quizId, questions) => staffAPI.post(`/admin/quizzes/${quizId}/questions/bulk`, questions),
  deleteQuizQuestion: (quizId, questionId) => staffAPI.delete(`/admin/quizzes/${quizId}/questions/${questionId}`),
  
  // Import
  importQuizQuestions: (quizId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return staffAPI.post(`/admin/quizzes/${quizId}/import-questions`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  
  // Assignments
  assignQuizToStudent: (quizId, data) => staffAPI.post(`/admin/quizzes/${quizId}/assign`, data),
  assignQuizToStudentsBulk: (quizId, data) => staffAPI.post(`/admin/quizzes/${quizId}/assign-bulk`, data),
  getQuizAssignments: (quizId) => staffAPI.get(`/admin/quizzes/${quizId}/assignments`),
  removeQuizAssignment: (quizId, assignmentId) => staffAPI.delete(`/admin/quizzes/${quizId}/assignments/${assignmentId}`),
  
  // Attempts
  getQuizAttempts: (quizId) => staffAPI.get(`/admin/quizzes/${quizId}/attempts`),
};

// ==================== Teacher Quiz Management ====================


export const teacherQuizAPI = {
  // =========================
  // QUIZ CRUD
  // =========================
  getMyQuizzes: () => staffAPI.get('/teacher/quizzes'),
  getQuiz: (quizId) => staffAPI.get(`/teacher/quizzes/${quizId}`),
  createQuiz: (data) => staffAPI.post('/teacher/quizzes', data),
  updateQuiz: (quizId, data) => staffAPI.put(`/teacher/quizzes/${quizId}`, data),
  deleteQuiz: (quizId) => staffAPI.delete(`/teacher/quizzes/${quizId}`),

  // =========================
  // STATUS
  // =========================
  updateQuizStatus: (quizId, status) =>
    staffAPI.put(`/teacher/quizzes/${quizId}/status`, { status }),

  // =========================
  // QUESTIONS
  // =========================
  getQuizQuestions: (quizId) =>
    staffAPI.get(`/teacher/quizzes/${quizId}/questions`),

  createQuizQuestionsBulk: (quizId, questions) =>
    staffAPI.post(`/teacher/quizzes/${quizId}/questions/bulk`, questions),

  deleteQuizQuestion: (quizId, questionId) =>
    staffAPI.delete(`/teacher/quizzes/${quizId}/questions/${questionId}`),

  importQuizQuestions: (quizId, file) => {
    const formData = new FormData();
    formData.append('file', file);

    return staffAPI.post(
      `/teacher/quizzes/${quizId}/import-questions`,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
      }
    );
  },

  // =========================
  // ASSIGNMENTS
  // =========================
  assignQuizToStudents: (quizId, data) =>
    staffAPI.post(`/teacher/quizzes/${quizId}/assign-bulk`, data),

  getQuizAssignments: (quizId) =>
    staffAPI.get(`/teacher/quizzes/${quizId}/assignments`),

  removeAssignment: (quizId, assignmentId) =>
    staffAPI.delete(
      `/teacher/quizzes/${quizId}/assignments/${assignmentId}`
    ),

  removeAssignmentsBulk: (quizId, assignmentIds) =>
    staffAPI.delete(
      `/teacher/quizzes/${quizId}/assignments/bulk`,
      { data: assignmentIds }
    ),

  removeAllAssignments: (quizId) =>
    staffAPI.delete(`/teacher/quizzes/${quizId}/assignments/all`),

  // =========================
  // ATTEMPTS
  // =========================
  getQuizAttempts: (quizId) =>
    staffAPI.get(`/teacher/quizzes/${quizId}/attempts`),

  // 
  toggleQuizStatus: (quizId, status) => staffAPI.put(`/teacher/quizzes/${quizId}/status`, { status }),
};

// ==================== Student Quiz ====================
export const studentQuizAPI = {
  getAssignedQuizzes: () => studentAPI.get('/student/quizzes/assigned'),
  getQuizDetails: (quizId) => studentAPI.get(`/student/quizzes/${quizId}`),
  startQuiz: (quizId) => studentAPI.post(`/student/quizzes/${quizId}/start`),
  getQuizQuestions: (attemptId) => studentAPI.get(`/student/quizzes/attempts/${attemptId}/questions`),
  getQuizAnswers: (attemptId) => studentAPI.get(`/student/quizzes/attempts/${attemptId}/answers`),
  submitAnswer: (attemptId, data) => studentAPI.post(`/student/quizzes/attempts/${attemptId}/answer`, data),
  submitQuiz: (attemptId) => studentAPI.post(`/student/quizzes/attempts/${attemptId}/submit`),
  getQuizAttempts: (quizId) => studentAPI.get(`/student/quizzes/${quizId}/attempts`),
  getAttemptReview: (attemptId) => studentAPI.get(`/student/quizzes/attempts/${attemptId}/review`),
  getTimeRemaining: (attemptId) => studentAPI.get(`/student/quizzes/attempts/${attemptId}/time-remaining`),
};

// ==================== Student Portal Services ====================
export const studentPortalAPI = {
  getProfile: () => studentAPI.get('/student-portal/profile'),
  getAcademicSessions: () => studentAPI.get('/student-portal/academic-sessions'),
  getGrades: (params = {}) => studentAPI.get('/student-portal/grades', { params }),
  getAssessments: (params = {}) => studentAPI.get('/student-portal/assessments', { params }),
  getSubjects: () => studentAPI.get('/student-portal/subjects'),
  getPerformanceSummary: () => studentAPI.get('/student-portal/performance-summary'),
  getSubjectPerformance: (subjectId) => studentAPI.get(`/student-portal/subject-performance/${subjectId}`),
  changePassword: (data) => studentAPI.post('/auth/student/change-password', data),
  // Quiz methods
  getAssignedQuizzes: studentQuizAPI.getAssignedQuizzes,
  getQuizDetails: studentQuizAPI.getQuizDetails,
  startQuiz: studentQuizAPI.startQuiz,
  getQuizQuestions: studentQuizAPI.getQuizQuestions,
  getQuizAnswers: studentQuizAPI.getQuizAnswers,
  submitAnswer: studentQuizAPI.submitAnswer,
  submitQuiz: studentQuizAPI.submitQuiz,
  getQuizAttempts: studentQuizAPI.getQuizAttempts,
  getAttemptReview: studentQuizAPI.getAttemptReview,
  getTimeRemaining: studentQuizAPI.getTimeRemaining,
};

// ==================== Auth Services ====================
export const authAPI = {
  // Add to adminQuizAPI object
removeQuizAssignment: (quizId, assignmentId) => 
  staffAPI.delete(`/admin/quizzes/${quizId}/assignments/${assignmentId}`),

removeQuizAssignmentsBulk: (quizId, assignmentIds) => 
  staffAPI.delete(`/admin/quizzes/${quizId}/assignments/bulk`, { data: assignmentIds }),

removeAllQuizAssignments: (quizId) => 
  staffAPI.delete(`/admin/quizzes/${quizId}/assignments/all`),
  login: (data) => staffAPI.post('/auth/login', data),
  register: (data) => staffAPI.post('/auth/register', data),
  changePassword: (data) => staffAPI.post('/auth/change-password', data),
  studentLogin: (data) => studentAPI.post('/auth/student-login', data),
  getMe: () => staffAPI.get('/auth/me'),
  logout: () => staffAPI.post('/auth/logout'),
  updateProfile: (data) => staffAPI.put('/auth/update-profile', data),
  updatePassword: (data) => staffAPI.put('/auth/update-password', data),
  googleAuth: (data) => staffAPI.post('/auth/google-auth', data),
};

// ==================== Student Auth Services ====================
export const studentAuthAPI = {
  login: (data) => studentAPI.post('/auth/student-login', data),
  changePassword: (data) => studentAPI.post('/auth/student/change-password', data),
  getProfile: () => studentAPI.get('/student-portal/profile'),
};

// ==================== Admin Services ====================
export const adminAPI = {
  // Bulk student actions
  bulkApproveStudents: (studentRegs) =>
    staffAPI.post('/admin/students/bulk-approve', { student_regs: studentRegs }),

  bulkRejectStudents: (studentRegs, reason) =>
    staffAPI.post('/admin/students/bulk-reject', { student_regs: studentRegs, reason }),

  getMasterResultSheet: (classId, sessionId, termId) =>
    staffAPI.get('/admin/master-result-sheet', {
      params: {
        class_id: classId,
        academic_session_id: sessionId,
        term_id: termId,
      },
    }),

  getMasterResultPivot: (classId, sessionId, termId) =>
    staffAPI.get('/admin/master-result-pivot', {
      params: { class_id: classId, academic_session_id: sessionId, term_id: termId },
    }),

  getStaff: () => staffAPI.get('/admin/staff'),
  createStaff: (data) => staffAPI.post('/admin/staff', data),
  updateStaff: (id, data) => staffAPI.put(`/admin/staff/${id}`, data),
  deleteStaff: (id) => staffAPI.delete(`/admin/staff/${id}`),
  updateStaffStatus: (id, status) => staffAPI.put(`/admin/staff/${id}/status`, { status }),
  
  getAcademicSessions: () => staffAPI.get('/admin/academic-sessions'),
  createAcademicSession: (data) => staffAPI.post('/admin/academic-sessions', data),
  updateAcademicSession: (id, data) => staffAPI.put(`/admin/academic-sessions/${id}`, data),
  saveAssessmentConfig: (data) => staffAPI.post(`/admin/assessment-config`, data),
  deleteAcademicSession: (id) => staffAPI.delete(`/admin/academic-sessions/${id}`),
  updateAcademicSessionStatus: (id, status) => staffAPI.put(`/admin/academic-sessions/${id}/status`, { academic_session_status: status }),
  
  createTerm: (sessionId, data) => staffAPI.post(`/admin/academic-sessions/${sessionId}/terms`, data),
  updateTermStatus: (termId, status) => staffAPI.put(`/admin/terms/${termId}/status`, { term_status: status }),
  deleteTerm: (termId) => staffAPI.delete(`/admin/terms/${termId}`),
  
  getStudents: (params) => staffAPI.get('/admin/students', { params }),
  createStudent: (data) => staffAPI.post('/admin/students', data),
  updateStudent: (studentReg, data) => staffAPI.put(`/admin/students/${studentReg}`, data),
  deleteStudent: (studentReg) => staffAPI.delete(`/admin/students/${studentReg}`),
  
  getClasses: () => staffAPI.get('/admin/classes'),
  createClass: (data) => staffAPI.post('/admin/classes', data),
  updateClass: (classId, data) => staffAPI.put(`/admin/classes/${classId}`, data),
  deleteClass: (classId) => staffAPI.delete(`/admin/classes/${classId}`),
  
  getSubjects: () => staffAPI.get('/admin/subjects'),
  createSubject: (data) => staffAPI.post('/admin/subjects', data),
  updateSubject: (subjectId, data) => staffAPI.put(`/admin/subjects/${subjectId}`, data),
  deleteSubject: (subjectId) => staffAPI.delete(`/admin/subjects/${subjectId}`),
  assignSubject: (data) => staffAPI.post('/admin/assign-subject', data), 
  
  getAssessmentFields: () => staffAPI.get('/admin/assessment-fields'),
  createAssessmentField: (data) => staffAPI.post('/admin/assessment-fields', data),
  updateAssessmentField: (id, data) => staffAPI.put(`/admin/assessment-fields/${id}`, data),
  deleteAssessmentField: (id) => staffAPI.delete(`/admin/assessment-fields/${id}`),

  downloadStudentTemplate: () =>
    staffAPI.get('/admin/download-student-template', {
      responseType: 'blob',
    }),

  getStudentTags: () => staffAPI.get('/admin/student-tags'),
  createStudentTag: (data) => staffAPI.post('/admin/student-tags', data),
  bulkCreateStudents: (data) => staffAPI.post('/admin/bulk-create-students', data, {
    headers: {}
  }),
  
  getStudentFields: () => staffAPI.get('/admin/student-fields'),
  changePasswordBulkStudent: (data) => staffAPI.post('/admin/students/bulk-reset-password', data),
  toggleStudentLoginBulk: (data) => staffAPI.post('/admin/students/login-toggle', data),
  generateStudentReg: (data) => staffAPI.post('/admin/generate-student-reg', data),
  getStudentsByTag: (tagId) => staffAPI.get(`/admin/students/by-tag/${tagId}`),
  getEnrolledStudents: () => staffAPI.get(`/admin/students/enrolled`),
  enrollStudent: (data) => staffAPI.post('/admin/student-enrollment', data),
  bulkEnrollStudents: (data) => staffAPI.post('/admin/bulk-enroll-students', data),
  getClassesDetailed: () => staffAPI.get(`/admin/classes/detailed`),
  getSubjectsWithAssignments: () => staffAPI.get(`/admin/subjects-with-assignments`),
  approveStudent: (studentReg) =>
    staffAPI.post(
      `/admin/students/approve`,
      null,
      {
        params: {
          student_reg: studentReg
        }
      }
    ),

  getResultTemplates: () => staffAPI.get('/admin/reports/templates'),
  
  uploadTemplate: (formData) => staffAPI.post('/admin/reports/templates', formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  }),
  
  previewTemplate: (formData) => staffAPI.post('/admin/reports/templates/preview', formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  }),
  
  generateStudentPDF: (config) => staffAPI.post('/admin/reports/generate-dynamic-pdf', config, {
    responseType: 'blob'
  }),

  generateBulkPDF: (config) => staffAPI.post('/admin/reports/generate-pdf/bulk', config, {
    responseType: 'blob'
  }),
  
  getReportHistory: (params) => staffAPI.get('/admin/reports/history', { params }),
  getReportStats: () => staffAPI.get('/admin/reports/stats'),
  deleteReport: (id) => staffAPI.delete(`/admin/reports/${id}`),
  
  getEnrolledStudents: (params) => staffAPI.get('/admin/students/enrolled', { params }),
  assignFormMaster: (data) => staffAPI.post('/admin/assign-form-master', data),
  getSessionTerms: (sessionId) => staffAPI.get(`/admin/academic-sessions/${sessionId}/terms`),

  getCommentFields: () => staffAPI.get('/admin/comment-fields'),
  createCommentField: (data) => staffAPI.post('/admin/comment-fields', data),
  updateCommentField: (id, data) => staffAPI.put(`/admin/comment-fields/${id}`, data),
  deleteCommentField: (id) => staffAPI.delete(`/admin/comment-fields/${id}`),

  deleteTemplate: async (templateId) => {
    try {
      const response = await staffAPI.delete(
        `/admin/reports/templates/${templateId}`
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  // Quiz methods
  getQuizzes: adminQuizAPI.getQuizzes,
  getMyQuizzes: adminQuizAPI.getMyQuizzes,
  getQuiz: adminQuizAPI.getQuiz,
  getTeacherQuiz: adminQuizAPI.getTeacherQuiz,
  createQuiz: adminQuizAPI.createQuiz,
  updateQuiz: adminQuizAPI.updateQuiz,
  updateTeacherQuiz: adminQuizAPI.updateTeacherQuiz,
  deleteQuiz: adminQuizAPI.deleteQuiz,
  deleteTeacherQuiz: adminQuizAPI.deleteTeacherQuiz,
  toggleQuizStatus: adminQuizAPI.toggleQuizStatus,
  getQuizQuestions: adminQuizAPI.getQuizQuestions,
  createQuizQuestionsBulk: adminQuizAPI.createQuizQuestionsBulk,
  deleteQuizQuestion: adminQuizAPI.deleteQuizQuestion,
  importQuizQuestions: adminQuizAPI.importQuizQuestions,
  assignQuizToStudent: adminQuizAPI.assignQuizToStudent,
  assignQuizToStudentsBulk: adminQuizAPI.assignQuizToStudentsBulk,
  getQuizAssignments: adminQuizAPI.getQuizAssignments,
  removeQuizAssignment: adminQuizAPI.removeQuizAssignment,
  getQuizAttempts: adminQuizAPI.getQuizAttempts,
};

// ==================== Teacher Services ====================
export const teacherAPI = {
  getAssignedSubjects: () => staffAPI.get('/subject-teacher/assigned-subjects'),
  getAssessmentData: (params) => staffAPI.get('/subject-teacher/assessment-data', { params }),
  captureAssessment: (data) => staffAPI.post('/subject-teacher/capture-assessment', data),
  getStudentProgress: (studentReg, subjectId) => staffAPI.get(`/subject-teacher/student-progress`, {
    params: { student_reg: studentReg, subject_id: subjectId }
  }),
  getAcademicSessions: () => staffAPI.get('/subject-teacher/academic-sessions'),
  getTerms: (sessionId) => staffAPI.get(`/subject-teacher/academic-sessions/${sessionId}/terms`),
  getClassesForSubject: (subjectId) => staffAPI.get(`/subject-teacher/classes-for-subject/${subjectId}`),
  
  getStudentsForGrading: (classId, subjectId, academicSessionId, termId) => 
    staffAPI.get(`/subject-teacher/students/${classId}/${subjectId}`, {
      params: { 
        academic_session_id: academicSessionId, 
        term_id: termId 
      }
    }),
  
  submitGrades: (classId, subjectId, academicSessionId, termId, data) => 
    staffAPI.post(`/subject-teacher/submit-grades/${classId}/${subjectId}`, data, {
      params: { 
        academic_session_id: academicSessionId, 
        term_id: termId 
      }
    }),
  
  getGradingSummary: (classId, subjectId, academicSessionId, termId) => 
    staffAPI.get(`/subject-teacher/grading-summary/${classId}/${subjectId}`, {
      params: { 
        academic_session_id: academicSessionId, 
        term_id: termId 
      }
    }),
  
  getSubjectRankings: (classId, subjectId, academicSessionId, termId) => 
    staffAPI.get(`/subject-teacher/subject-rankings/${classId}/${subjectId}`, {
      params: { 
        academic_session_id: academicSessionId, 
        term_id: termId 
      }
    }),
  
  getClassPerformanceAnalysis: (classId, subjectId, academicSessionId, termId) => 
    staffAPI.get(`/subject-teacher/class-performance-analysis/${classId}/${subjectId}`, {
      params: { 
        academic_session_id: academicSessionId, 
        term_id: termId 
      }
    }),
  
  getStudentGradesHistory: (studentReg, subjectId) => 
    staffAPI.get(`/subject-teacher/student-grades/${studentReg}/${subjectId}`),
  
  getDashboardStats: () => staffAPI.get('/subject-teacher/dashboard-stats'),

  downloadAssessmentTemplate: (params) => {
    return staffAPI.get('/subject-teacher/download-assessment-template', {
      params,
      responseType: 'blob'
    });
  },
  
  uploadAssessmentScores: (sessionId, termId, subjectId, classId, formData, config) => {
    const params = new URLSearchParams({
      academic_session_id: sessionId,
      term_id: termId,
      subject_id: subjectId,
      class_id: classId
    }).toString();
    
    return staffAPI.post(
      `/subject-teacher/upload-assessment-scores?${params}`,
      formData,
      {
        ...config,
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      }
    );
  },
  
  exportAssessmentData: (params) => {
    return staffAPI.get('/subject-teacher/export-assessment-data', {
      params,
      responseType: 'blob'
    });
  },

  // Quiz methods
  getMyQuizzes: teacherQuizAPI.getMyQuizzes,
  getQuiz: teacherQuizAPI.getQuiz,
  createQuiz: teacherQuizAPI.createQuiz,
    toggleQuizStatus: teacherQuizAPI.toggleQuizStatus,
  updateQuiz: teacherQuizAPI.updateQuiz,
  deleteQuiz: teacherQuizAPI.deleteQuiz,
  getQuizQuestions: teacherQuizAPI.getQuizQuestions,
  createQuizQuestionsBulk: teacherQuizAPI.createQuizQuestionsBulk,
  deleteQuizQuestion: teacherQuizAPI.deleteQuizQuestion,
  importQuizQuestions: teacherQuizAPI.importQuizQuestions,
  assignQuizToStudents: teacherQuizAPI.assignQuizToStudents,
  getQuizAssignments: teacherQuizAPI.getQuizAssignments,
  getQuizAttempts: teacherQuizAPI.getQuizAttempts,
};

// ==================== Form Master Services ====================
export const formMasterAPI = {
  getMyClasses: () => staffAPI.get('/form-master/my-classes'),
  getClassStudents: (params) => staffAPI.get('/form-master/class-students', { params }),
  calculateGrades: (data) => staffAPI.post('/form-master/calculate-grades', data),
  
  getMasterResultSheet: (classId, sessionId, termId) =>
    staffAPI.get('/form-master/master-result-sheet', {
      params: {
        class_id: classId,
        academic_session_id: sessionId,
        term_id: termId,
      },
    }),

  getMasterResultPivot: (classId, sessionId, termId) =>
    staffAPI.get('/form-master/master-result-pivot', {
      params: { class_id: classId, academic_session_id: sessionId, term_id: termId },
    }),
    
  getStudentComment: (studentReg, classId, academicSessionId, termId) => 
    staffAPI.get('/form-master/student-comment', {
        params: {
            student_reg: studentReg,
            class_id: classId,
            academic_session_id: academicSessionId,
            term_id: termId
        }
    }),
  
  addComment: (data) => staffAPI.post('/form-master/add-comment', data),
  
  getClassPerformance: (params) => staffAPI.get('/form-master/class-performance', { params }),
  getAcademicSessions: () => staffAPI.get('/form-master/academic-sessions'),
  getFormMasterStats: () => staffAPI.get('/form-master/dashboard'),
  generateClassReport: (data) => staffAPI.post('/form-master/generate-class-report', data),
  getTerms: (sessionId) => staffAPI.get(`/form-master/academic-sessions/${sessionId}/terms`),
  getCommentFields: () => staffAPI.get('/form-master/comment-fields'),
};

// ==================== Common Services ====================
export const commonAPI = {
  getAcademicSessions: () => staffAPI.get('/common/academic-sessions'),
  getTerms: (sessionId) => staffAPI.get(`/admin/academic-sessions/${sessionId}/terms`),
};

// ==================== Dashboard Services ====================
export const dashboardAPI = {
  getAdminStats: () => staffAPI.get('/dashboard/admin/stats'),
  getTeacherStats: () => staffAPI.get('/dashboard/teacher/stats'),
  getFormMasterStats: () => staffAPI.get('/form-master/stats'),
  getStudentStats: (studentReg) => staffAPI.get(`/dashboard/student/stats/${studentReg}`),
};

// ==================== Report Services ====================
export const reportAPI = {
  getSchoolConfig: () => staffAPI.get('/admin/reports/school-config'),
  updateSchoolConfig: (config) => staffAPI.post('/admin/reports/school-config', config),
  
  getTemplates: () => staffAPI.get('/admin/reports/templates'),
  uploadTemplate: (formData) => staffAPI.post('/admin/reports/templates', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  getTemplateById: (templateId) => staffAPI.get(`/admin/reports/templates/${templateId}`),
  updateTemplate: (templateId, formData) => staffAPI.put(`/admin/reports/templates/${templateId}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  deleteTemplate: (templateId) => staffAPI.delete(`/admin/reports/templates/${templateId}`),
  setDefaultTemplate: (templateId) => staffAPI.post(`/admin/reports/templates/${templateId}/set-default`),
  previewTemplate: (templateId) => staffAPI.get(`/admin/reports/templates/${templateId}/preview`),
  downloadTemplate: (templateId) => staffAPI.get(`/admin/reports/templates/${templateId}/download`, {
    responseType: 'blob'
  }),
  
  previewWithData: (html, data) => staffAPI.post('/admin/reports/templates/preview', {
    html,
    data: JSON.stringify(data)
  }),
  
  testPDF: () => staffAPI.get('/admin/reports/test-pdf'),
  
  generateStudentPDF: (data) => staffAPI.post('/admin/reports/generate-pdf/student', data, {
    responseType: 'blob'
  }),
  generateBulkPDF: (data) => staffAPI.post('/admin/reports/generate-pdf/bulk', data, {
    responseType: 'blob'
  })
};

// ==================== Master Admin API ====================
const masterAPI = axios.create({
  baseURL: API_URL,
});

masterAPI.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('master_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

masterAPI.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      toast.error('Master admin session expired. Please login again.');
      localStorage.removeItem('master_token');
      localStorage.removeItem('master_user');
      window.location.href = '/master/login';
    }
    return Promise.reject(error);
  }
);

export const masterAdminAPI = {
  login: (data) => masterAPI.post('/master/auth/login', data),
  getPendingSchools: () => masterAPI.get('/master/schools/pending'),
  approveSchool: (id) => masterAPI.put(`/master/schools/${id}/approve`),
  rejectSchool: (id) => masterAPI.put(`/master/schools/${id}/reject`),
  getAllSchools: () => masterAPI.get('/master/schools'),
  getSchoolDetail: (id) => masterAPI.get(`/master/schools/${id}`),
  addCredits: (id, data) => masterAPI.post(`/master/schools/${id}/add-credits`, data),
  suspendSchool: (id) => masterAPI.put(`/master/schools/${id}/suspend`),
  unsuspendSchool: (id) => masterAPI.put(`/master/schools/${id}/unsuspend`),
  getAllPayments: () => masterAPI.get('/master/payments'),
  getSchoolPayments: (id) => masterAPI.get(`/master/schools/${id}/payments`),
  getSchoolTransactions: (id) => masterAPI.get(`/master/schools/${id}/transactions`),
  getAllTransactions: () => masterAPI.get('/master/transactions'),
  getManualRequests: () => masterAPI.get('/payments/manual/requests'),
  approveManualRequest: (data) => masterAPI.post('/payments/manual/approve', data),
};

// ==================== School Admin API ====================
export const schoolAdminAPI = {
  getSchoolInfo: () => staffAPI.get('/schools/me'),
  getWallet: () => staffAPI.get('/schools/me/wallet'),
  getTransactions: () => staffAPI.get('/schools/me/transactions'),
  getPayments: () => staffAPI.get('/schools/me/payments'),
  getUsage: () => staffAPI.get('/schools/me/usage'),
  requestManualTopup: (data) => staffAPI.post('/payments/manual/request', data),
  initiatePayment: (data) => staffAPI.post('/payments/initiate', data),
  verifyPayment: (ref) => staffAPI.get(`/payments/verify/${ref}`),
};

// For backward compatibility
const api = staffAPI;
export default api;
